import type { FunctionalComponent } from 'preact';
import { useState, useCallback } from 'preact/hooks';
import { useStore } from '../store';
import {
  getEventOccurrenceEntries,
  getPurchasableOccurrenceEntries,
  resolveDisplayOccurrenceIso,
} from '../lib/eventOccurrenceDates';
import type { EventOccurrenceEntry } from '../lib/eventOccurrenceDates';
import { EventDateSelectModalWidget } from './EventDateSelectModalWidget';
import { applyOccurrenceDateSelection } from '../lib/applyOccurrenceSelection';

function formatHeroDate(iso: string, tz?: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz || undefined });
  } catch {
    return '';
  }
}

/** Desktop hero: bottom-left “change date” control; opens same modal as table flow. */
export const EventHeroDateControl: FunctionalComponent = () => {
  const { state, dispatch } = useStore();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const event = state.event;
  const widget = state.widget;
  if (!event || !widget?.show_event_image) return null;

  const entries = getEventOccurrenceEntries(event as unknown);
  const purchasable = getPurchasableOccurrenceEntries(entries);
  if (purchasable.length <= 1) return null;

  const tz = event.timezone;
  const currentIso = resolveDisplayOccurrenceIso(event, state.selectedOccurrenceIso);
  const label = formatHeroDate(currentIso, tz);

  const onConfirm = useCallback(
    async (entry: EventOccurrenceEntry) => {
      setBusy(true);
      try {
        await applyOccurrenceDateSelection(dispatch, state.widgetId, event, entry, {
          widgetBootstrapEventId: state.widgetBootstrapEventId,
          widgetBootstrapFirstOccurrenceIso: state.widgetBootstrapFirstOccurrenceIso,
        });
        setOpen(false);
      } catch (e) {
        console.error(e);
      } finally {
        setBusy(false);
      }
    },
    [dispatch, state.widgetId, event, state.widgetBootstrapEventId, state.widgetBootstrapFirstOccurrenceIso],
  );

  return (
    <>
      <button
        type="button"
        class="mw-hero-date-chip"
        onClick={() => !busy && setOpen(true)}
        disabled={busy}
      >
        <span class="mw-hero-date-chip__label">Event date: {label}</span>
        <span class="mw-hero-date-chip__action">Change</span>
      </button>
      <EventDateSelectModalWidget
        open={open}
        onClose={() => !busy && setOpen(false)}
        occurrences={purchasable}
        selectedIso={currentIso}
        onConfirm={(entry) => void onConfirm(entry)}
        timezone={tz}
      />
    </>
  );
};
